import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useSite } from "../context/SiteContext";
import { fetchEvents } from "../lib/api";
import { langField } from "../lib/i18n";

export default function Events() {
  const { t, lang, formatPrice } = useApp();
  const { s } = useSite();
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchEvents().then(setEvents);
  }, []);

  const filtered = events.filter((e) => filter === "all" || e.type === filter);
  const freeLabel = s(`events_free_label_${lang}`, lang === "pt" ? "GRÁTIS" : "FREE");

  return (
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-12 md:py-20" data-testid="page-events">
      <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ EVENTS</p>
      <h1 className="font-display text-6xl md:text-8xl mt-2 mb-3 leading-none">{t.events.title}</h1>
      <p className="text-kult-muted max-w-xl mb-10">{t.events.sub}</p>

      <div className="flex gap-2 mb-8 border-y border-kult-border py-4">
        {[
          { k: "all", en: "ALL", pt: "TUDO" },
          { k: "wrestling", en: "WRESTLING", pt: "WRESTLING" },
          { k: "music", en: "MUSIC", pt: "MÚSICA" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            className={`font-display text-lg px-5 py-2 border-2 ${
              filter === f.k
                ? "bg-kult-pink text-black border-kult-pink"
                : "border-kult-border hover:border-kult-pink"
            }`}
            data-testid={`events-filter-${f.k}`}
          >
            {lang === "pt" ? f.pt : f.en}
          </button>
        ))}
      </div>

      <div className="space-y-4" data-testid="events-grid">
        {filtered.map((e) => {
          const d = new Date(e.date);
          return (
            <article
              key={e.id}
              className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 border border-kult-border p-5 md:p-6 hover:border-kult-pink transition-colors group"
              data-testid={`event-card-${e.id}`}
            >
              <div className="md:col-span-3 aspect-video md:aspect-square bg-black overflow-hidden">
                <img
                  src={e.image}
                  alt={e.title}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all"
                />
              </div>
              <div className="md:col-span-7 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-mono text-xs tracking-[0.25em] bg-kult-yellow text-black px-2 py-0.5">
                    {e.type === "wrestling" ? "WRESTLING" : "MUSIC"}
                  </span>
                  <span className="font-mono text-xs tracking-[0.25em] text-kult-muted">
                    {d.toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB", {
                      weekday: "short",
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    }).toUpperCase()}
                  </span>
                </div>
                <h3 className="font-display text-3xl md:text-4xl leading-tight">
                  {langField(e, "title", lang)}
                </h3>
                <p className="text-kult-muted mt-3">
                  {langField(e, "description", lang)}
                </p>
                <p className="font-mono text-xs tracking-[0.25em] text-white mt-3">
                  {e.city.toUpperCase()} / {e.venue.toUpperCase()} ·{" "}
                  {d.toLocaleTimeString(lang === "pt" ? "pt-PT" : "en-GB", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
              <div className="md:col-span-2 flex md:justify-end items-center">
                {(() => {
                  const mode = e.ticket_mode || (e.is_free ? "free" : e.ticket_url ? "external" : "stripe");
                  if (mode === "free") {
                    return (
                      <div
                        className="bg-kult-yellow text-black px-6 py-3 font-display text-xl w-full md:w-auto text-center"
                        data-testid={`event-free-${e.id}`}
                      >
                        ⚡ {freeLabel}
                      </div>
                    );
                  }
                  if (mode === "external" && e.ticket_url) {
                    return (
                      <div className="flex flex-col items-stretch md:items-end gap-1 w-full md:w-auto">
                        {e.ticket_price != null && (
                          <span className="font-mono text-[10px] tracking-[0.25em] text-kult-yellow md:text-right" data-testid={`event-from-price-${e.id}`}>
                            {lang === "pt" ? "DESDE" : "FROM"} <span className="text-white">{formatPrice(Number(e.ticket_price))}</span>
                          </span>
                        )}
                        <a
                          href={e.ticket_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-kult-pink text-black px-6 py-3 font-display text-xl hover:bg-kult-yellow transition-colors text-center"
                          data-testid={`event-tickets-${e.id}`}
                        >
                          {t.events.tickets} ↗
                        </a>
                      </div>
                    );
                  }
                  return (
                    <Link
                      to={`/tickets?event=${e.id}`}
                      className="bg-kult-pink text-black px-6 py-3 font-display text-xl hover:bg-kult-yellow transition-colors w-full md:w-auto text-center"
                      data-testid={`event-tickets-${e.id}`}
                    >
                      {t.events.tickets}
                    </Link>
                  );
                })()}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
